/**
 * Daybreak Bundle - Shopify JavaScript
 * Copy this file to your theme's assets/ folder
 * Include in theme.liquid before </body>: <script src="{{ 'daybreak.js' | asset_url }}" defer></script>
 */

(function() {
'use strict';

// ============================================
// AUDIO PLAYER
// ============================================
class AudioPlayer {
  constructor() {
    this.currentlyPlaying = null;
    this.audioElements = {};
    this.progressIntervals = {};
    this.init();
  }

  init() {
    document.addEventListener('click', (e) => {
      const playBtn = e.target.closest('[data-audio-play]');
      if (playBtn) {
        this.handlePlayClick(playBtn);
      }

      const waveform = e.target.closest('[data-audio-waveform]');
      if (waveform) {
        this.handleWaveformClick(e, waveform);
      }

      // Scrubber bar click handler (for master preview)
      const scrubber = e.target.closest('[data-audio-scrubber]');
      if (scrubber) {
        this.handleScrubberClick(e, scrubber);
      }
    });
  }

  handlePlayClick(btn) {
    const audioId = btn.dataset.audioPlay;
    const audioUrl = btn.dataset.audioUrl;

    if (!audioUrl) return;

    if (this.currentlyPlaying === audioId) {
      this.pause(audioId);
    } else {
      if (this.currentlyPlaying) {
        this.pause(this.currentlyPlaying);
      }
      this.play(audioId, audioUrl);
    }
  }

  play(audioId, audioUrl) {
    let audio = this.audioElements[audioId];

    if (!audio) {
      audio = new Audio(audioUrl);
      this.audioElements[audioId] = audio;

      audio.addEventListener('loadedmetadata', () => {
        this.updateDuration(audioId, audio.duration);
      });

      audio.addEventListener('ended', () => {
        this.onEnded(audioId);
      });
    }

    audio.play();
    this.currentlyPlaying = audioId;
    this.setPlayingState(audioId, true);
    this.startProgressUpdate(audioId, audio);
  }

  pause(audioId) {
    const audio = this.audioElements[audioId];
    if (audio) {
      audio.pause();
    }
    this.currentlyPlaying = null;
    this.setPlayingState(audioId, false);
    this.stopProgressUpdate(audioId);
  }

  onEnded(audioId) {
    this.currentlyPlaying = null;
    this.setPlayingState(audioId, false);
    this.stopProgressUpdate(audioId);
    this.updateProgress(audioId, 0);
  }

  setPlayingState(audioId, isPlaying) {
    const playerContainer = document.querySelector(`[data-audio-player="${audioId}"]`);
    const playBtn = document.querySelector(`[data-audio-play="${audioId}"]`);
    if (playerContainer) {
      playerContainer.classList.toggle('is-playing', isPlaying);
    }
    if (playBtn) {
      playBtn.classList.toggle('is-playing', isPlaying);
      const playIcon = playBtn.querySelector('.play-icon');
      const pauseIcon = playBtn.querySelector('.pause-icon');
      if (playIcon) playIcon.style.display = isPlaying ? 'none' : 'block';
      if (pauseIcon) pauseIcon.style.display = isPlaying ? 'block' : 'none';
    }
  }

  startProgressUpdate(audioId, audio) {
    this.stopProgressUpdate(audioId);
    this.progressIntervals[audioId] = setInterval(() => {
      if (audio.duration) {
        const progress = audio.currentTime / audio.duration;
        this.updateProgress(audioId, progress);
      }
    }, 50);
  }

  stopProgressUpdate(audioId) {
    if (this.progressIntervals[audioId]) {
      clearInterval(this.progressIntervals[audioId]);
      delete this.progressIntervals[audioId];
    }
  }

  updateProgress(audioId, progress) {
    // Waveform bars (for pack preview players)
    const waveform = document.querySelector(`[data-audio-waveform="${audioId}"]`);
    if (waveform) {
      const bars = waveform.querySelectorAll('.db-waveform-bar');
      bars.forEach((bar, i) => {
        const barProgress = i / bars.length;
        const isActive = barProgress < progress;
        bar.classList.toggle('is-active', isActive);

        // Daybreak style: warm white inactive, bright white active
        bar.style.setProperty(
          'background-color',
          isActive ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.2)',
          'important'
        );
      });
    }

    // Scrubber bar (for master preview player)
    const scrubber = document.querySelector(`[data-audio-scrubber="${audioId}"]`);
    if (scrubber) {
      const fill = scrubber.querySelector('.db-scrubber-fill');
      if (fill) {
        // Use setProperty with !important to override init styles
        fill.style.setProperty('width', `${progress * 100}%`, 'important');
      }
    }

    // Update duration display with current time for scrubber-style players
    const audio = this.audioElements[audioId];
    if (audio && audio.duration) {
      const durationEl = document.querySelector(`[data-audio-duration="${audioId}"]`);
      if (durationEl && durationEl.textContent.includes('/')) {
        // Format as "current / total" for scrubber-style players
        durationEl.textContent = `${this.formatTime(audio.currentTime)} / ${this.formatTime(audio.duration)}`;
      }
    }
  }

  updateDuration(audioId, duration) {
    const durationEl = document.querySelector(`[data-audio-duration="${audioId}"]`);
    if (durationEl) {
      // Check if it's a scrubber-style player (has "/" in default text)
      if (durationEl.textContent.includes('/')) {
        durationEl.textContent = `0:00 / ${this.formatTime(duration)}`;
      } else {
        durationEl.textContent = this.formatTime(duration);
      }
    }
  }

  formatTime(seconds) {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  handleWaveformClick(e, waveform) {
    const audioId = waveform.dataset.audioWaveform;
    const audio = this.audioElements[audioId];
    if (!audio || !audio.duration) return;

    const rect = waveform.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const progress = Math.max(0, Math.min(1, clickX / rect.width));
    audio.currentTime = progress * audio.duration;
    this.updateProgress(audioId, progress);
  }

  handleScrubberClick(e, scrubber) {
    const audioId = scrubber.dataset.audioScrubber;
    const audio = this.audioElements[audioId];
    if (!audio || !audio.duration) return;

    const rect = scrubber.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const progress = Math.max(0, Math.min(1, clickX / rect.width));
    audio.currentTime = progress * audio.duration;
    this.updateProgress(audioId, progress);
  }
}

// ============================================
// STICKY BAR
// ============================================

class StickyBar {
  constructor() {
    this.stickyBar = document.querySelector('.db-sticky-bar');
    this.triggerElement = document.getElementById('audio-preview');
    this.triggerOffset = 100;
    if (this.stickyBar) {
      this.init();
    }
  }

  init() {
    window.addEventListener('scroll', () => this.handleScroll(), { passive: true });
    this.handleScroll();
  }

  handleScroll() {
    if (!this.triggerElement) {
      // Fallback: show after scrolling 500px
      const shouldShow = window.scrollY > 500;
      this.stickyBar.classList.toggle('is-visible', shouldShow);
      return;
    }

    const rect = this.triggerElement.getBoundingClientRect();
    const shouldShow = rect.top <= this.triggerOffset;
    this.stickyBar.classList.toggle('is-visible', shouldShow);
  }
}

// ============================================
// FAQ ACCORDION
// ============================================

class FAQAccordion {
  constructor() {
    this.init();
  }

  init() {
    document.addEventListener('click', (e) => {
      const question = e.target.closest('.db-faq-question');
      if (question) {
        this.toggle(question);
      }
    });
  }

  toggle(questionBtn) {
    const faqItem = questionBtn.closest('.db-faq-item');
    if (!faqItem) return;

    const isOpen = faqItem.classList.contains('is-open');
    // Close all other items (optional - remove for multi-open)
    document.querySelectorAll('.db-faq-item.is-open').forEach(item => {
      if (item !== faqItem) {
        item.classList.remove('is-open');
      }
    });

    faqItem.classList.toggle('is-open', !isOpen);
  }
}

// ============================================
// LAZY LOADING WITH INTERSECTION OBSERVER
// ============================================

class LazyLoader {
  constructor() {
    this.init();
  }

  init() {
    const lazyElements = document.querySelectorAll('[data-lazy]');
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.load(entry.target);
            observer.unobserve(entry.target);
          }
        });
      }, {
        rootMargin: '100px'
      });

      lazyElements.forEach(el => observer.observe(el));
    } else {
      // Fallback for older browsers
      lazyElements.forEach(el => this.load(el));
    }
  }

  load(element) {
    // Load lazy images
    const lazySrc = element.dataset.lazySrc;
    if (lazySrc && element.tagName === 'IMG') {
      element.src = lazySrc;
    }

    // Remove skeleton/placeholder classes
    element.classList.remove('is-loading');
    element.classList.add('is-loaded');
    element.removeAttribute('data-lazy');
  }
}

// ============================================
// MARQUEE ANIMATION CONTROL
// ============================================

class MarqueeController {
  constructor() {
    this.marquees = document.querySelectorAll('.db-marquee');
    this.init();
  }

  init() {
    // Pause on hover (optional)
    this.marquees.forEach(marquee => {
      marquee.addEventListener('mouseenter', () => {
        marquee.style.animationPlayState = 'paused';
      });
      marquee.addEventListener('mouseleave', () => {
        marquee.style.animationPlayState = 'running';
      });
    });

    // Reduce motion for users who prefer it
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.marquees.forEach(marquee => {
        marquee.style.animation = 'none';
      });
    }
  }
}

// ============================================
// SCROLL ANIMATIONS
// ============================================

class ScrollAnimations {
  constructor() {
    this.init();
  }

  init() {
    const animatedElements = document.querySelectorAll('[data-animate-on-scroll]');
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('db-animate-fade-in');
            observer.unobserve(entry.target);
          }
        });
      }, {
        threshold: 0.1
      });

      animatedElements.forEach(el => observer.observe(el));
    }
  }
}

// ============================================
// WAVEFORM GENERATOR (Optional Enhancement)
// ============================================

function generateWaveformBars(container, data) {
  if (!container) return;

  // Ensure container is visible with flex layout - reduced gap for more bars
  container.style.setProperty('display', 'flex', 'important');
  container.style.setProperty('align-items', 'center', 'important');
  container.style.setProperty('justify-content', 'space-between', 'important');
  container.style.setProperty('gap', '1px', 'important');
  container.style.setProperty('height', '32px', 'important');
  container.style.setProperty('min-height', '32px', 'important');
  container.style.setProperty('box-sizing', 'border-box', 'important');
  container.style.setProperty('padding', '0 2px', 'important');
  container.style.setProperty('visibility', 'visible', 'important');
  container.style.setProperty('opacity', '1', 'important');

  // If no waveform data, just make existing Liquid bars visible
  if (!data) {
    container.querySelectorAll('.db-waveform-bar').forEach((bar) => {
      bar.style.setProperty('opacity', '1', 'important');
      bar.style.setProperty('visibility', 'visible', 'important');
      bar.style.setProperty('display', 'block', 'important');
      bar.style.setProperty('background-color', 'rgba(255, 255, 255, 0.2)', 'important');
    });
    return;
  }

  // Interpolate data to double the bar count
  const interpolatedData = [];
  for (let i = 0; i < data.length; i++) {
    interpolatedData.push(data[i]);
    // Add interpolated value between current and next
    if (i < data.length - 1) {
      const avg = (data[i] + data[i + 1]) / 2;
      // Add slight variation for natural look
      interpolatedData.push(avg * (0.9 + Math.random() * 0.2));
    }
  }
  // Add one more at the end
  interpolatedData.push(data[data.length - 1] * 0.85);

  container.innerHTML = '';
  interpolatedData.forEach((height) => {
    const bar = document.createElement('div');
    bar.className = 'db-waveform-bar';
    // Thinner bars (max 3px), more rounded, match Daybreak style
    bar.style.cssText = `
      height: ${Math.max(height * 100, 12)}% !important;
      min-height: 4px !important;
      flex: 1 1 0 !important;
      min-width: 1px !important;
      max-width: 3px !important;
      border-radius: 1.5px !important;
      opacity: 1 !important;
      visibility: visible !important;
      display: block !important;
      background-color: rgba(255, 255, 255, 0.2) !important;
      transition: background-color 0.1s ease !important;
    `;

    container.appendChild(bar);
  });
}

// ============================================
// SCRUBBER SAFETY STYLES (prevents theme CSS from hiding it)
// ============================================

function initializeScrubbers() {
  // Also ensure the parent player container is visible
  document.querySelectorAll('.db-player--master').forEach((player) => {
    player.style.setProperty('display', 'flex', 'important');
    player.style.setProperty('visibility', 'visible', 'important');
    player.style.setProperty('opacity', '1', 'important');
  });

  document.querySelectorAll('[data-audio-scrubber]').forEach((scrubber) => {
    // Force visibility with !important to override any theme CSS
    scrubber.style.setProperty('display', 'block', 'important');
    scrubber.style.setProperty('position', 'relative', 'important');
    scrubber.style.setProperty('height', '6px', 'important');
    scrubber.style.setProperty('min-height', '6px', 'important');
    scrubber.style.setProperty('width', '100%', 'important');
    scrubber.style.setProperty('border-radius', '9999px', 'important');
    scrubber.style.setProperty('overflow', 'hidden', 'important');
    scrubber.style.setProperty('background-color', 'rgba(255, 255, 255, 0.1)', 'important');
    scrubber.style.setProperty('cursor', 'pointer', 'important');
    scrubber.style.setProperty('visibility', 'visible', 'important');
    scrubber.style.setProperty('opacity', '1', 'important');

    let fill = scrubber.querySelector('.db-scrubber-fill');
    if (!fill) {
      fill = document.createElement('div');
      fill.className = 'db-scrubber-fill';
      scrubber.appendChild(fill);
    }

    // Force fill visibility
    fill.style.setProperty('display', 'block', 'important');
    fill.style.setProperty('position', 'absolute', 'important');
    fill.style.setProperty('left', '0', 'important');
    fill.style.setProperty('top', '0', 'important');
    fill.style.setProperty('bottom', '0', 'important');
    fill.style.setProperty('height', '100%', 'important');
    // Don't set width here with !important - let updateProgress control it
    fill.style.setProperty('width', '0%');
    fill.style.setProperty('border-radius', '9999px', 'important');
    fill.style.setProperty('background-color', 'var(--db-accent-gold)', 'important');
    fill.style.setProperty('visibility', 'visible', 'important');
    fill.style.setProperty('opacity', '1', 'important');
  });

  console.info('[Daybreak] Scrubbers initialized:', document.querySelectorAll('[data-audio-scrubber]').length);
}

// Pre-defined waveform data (from original React implementation)
const waveformData = {
  "b-time-absent-em-88-01": [0.93,0.89,1,0.91,0.88,0.89,0.82,0.88,0.92,0.87,0.9,1,0.89,0.95,0.81,0.81,0.83,0.83,0.73,0.76,0.78,0.73,0.72,0.67,0.94,0.88,0.95,0.6,0.82,0.92,0.89,0.87,0.56,0.37,0.11],
  "b-time-memories-forgotten-g-88-01": [0.72,0.46,0.55,0.34,0.79,0.7,0.8,0.58,0.83,0.72,0.36,0.6,0.49,0.78,0.64,0.74,0.75,1,0.67,0.55,0.34,0.58,0.8,0.65,0.76,0.64,0.86,0.53,0.59,0.41,0.75,0.62,0.53,0.28,0.14],
  "b-time-suffering-bm-78-01": [0.38,0.5,0.66,0.6,0.52,0.59,0.7,0.7,0.63,0.45,0.47,0.7,0.56,0.51,0.65,0.71,0.7,0.6,0.63,0.76,0.79,0.79,0.69,0.67,0.53,0.66,0.61,0.7,0.75,0.76,0.84,1,0.8,0.47,0.17],
  "b-time-water-cycle-bbm-73-01": [0.66,0.47,0.81,0.93,0.69,0.52,0.74,0.79,0.99,0.6,0.53,0.76,0.91,0.66,0.5,0.83,0.74,0.89,0.52,0.67,0.8,0.81,0.63,0.52,0.77,1,0.79,0.49,0.8,0.9,0.68,0.43,0.34,0.28,0.09],
  "g-chops-aurora-fm-77-01": [1,0.93,0.91,0.84,0.93,0.96,0.9,0.91,0.89,1,0.9,0.9,0.79,0.58,0.88,0.91,0.96,0.61,0.79,0.89,0.87,0.71,0.6,0.92,0.9,0.8,0.61,0.86,0.91,0.79,0.72,0.61,0.55,0.35,0.12],
  "g-chops-cornucopia-gm-79-01": [0.41,0.41,0.48,0.42,0.41,0.61,0.39,0.49,0.42,0.54,0.42,0.66,0.47,0.64,0.48,0.53,0.83,0.79,0.58,0.39,0.88,0.39,0.46,0.4,0.49,0.41,0.69,0.88,1,0.65,0.99,0.54,0.45,0.49,0.19],
  "g-chops-doldrums-em-84-01": [0.85,0.82,0.79,0.62,0.6,0.35,0.92,0.7,0.87,0.76,0.79,0.67,0.62,0.58,0.48,1,0.65,0.57,0.32,0.39,0.31,0.49,0.45,0.47,0.35,0.44,0.42,0.4,0.27,0.34,0.4,0.28,0.22,0.1,0.04],
  "g-chops-telekinesis-fm-87-01": [0.76,0.64,0.49,0.84,0.84,0.56,0.52,0.4,0.83,0.55,0.71,0.37,1,0.64,0.56,0.69,0.83,0.73,0.61,0.69,0.59,0.68,0.43,0.63,0.82,0.88,0.77,0.61,0.52,0.58,0.65,0.36,0.35,0.3,0.11],
  "v1-go-on-then-em-77-01": [0.64,0.52,1,0.66,0.85,0.58,0.92,0.8,0.86,0.61,0.78,0.77,0.65,0.78,0.54,0.91,0.74,0.98,0.6,0.88,0.86,0.97,0.66,0.73,0.93,0.94,0.96,0.53,0.89,0.61,0.65,0.34,0.33,0.22,0.11],
  "v1-home-so-soon-am-75-01": [0.59,0.52,0.45,0.48,0.59,0.56,0.49,0.45,0.49,0.5,0.43,0.47,0.43,0.53,0.42,0.48,0.49,0.59,0.77,0.89,1,0.66,0.67,0.88,0.98,0.81,0.56,0.79,0.78,0.81,0.63,0.74,0.82,0.56,0.17],
  "v1-prophetic-dm-78-01": [0.94,0.61,0.83,0.55,0.81,0.55,0.81,0.58,0.81,0.79,0.73,0.68,0.53,0.83,0.55,0.45,0.44,0.94,0.7,0.77,0.55,0.87,0.6,0.69,0.81,0.69,1,0.64,0.79,0.5,0.75,0.34,0.39,0.17,0.07],
  "v1-repair-yourself-c-m-81-01": [0.92,0.71,0.54,0.46,0.54,0.53,0.63,0.57,0.9,0.45,0.48,0.52,0.48,0.54,0.6,0.59,0.88,0.94,0.84,0.98,0.58,0.72,0.9,0.92,0.83,0.46,0.85,0.81,1,0.38,0.89,0.71,0.53,0.26,0.07],
  "v2-he-is-able-am-80-01": [0.98,0.7,0.83,0.99,0.95,0.82,0.75,0.88,0.92,0.74,0.74,0.76,0.76,0.83,0.74,1,0.94,0.93,0.75,0.79,0.86,0.95,0.97,0.69,0.83,0.98,0.82,0.75,0.75,0.77,0.66,0.46,0.35,0.32,0.07],
  "v2-i-know-a-place-c-76-01": [0.81,0.72,0.87,0.76,0.81,1,0.86,0.81,0.69,0.81,0.73,0.91,0.7,0.96,0.89,0.84,0.79,0.68,0.94,0.88,0.92,0.96,0.96,0.8,0.91,0.87,0.83,0.89,0.89,0.65,0.62,0.41,0.32,0.18,0.05],
  "v2-old-fashioned-f-m-88-01": [0.9,0.87,0.82,0.77,0.92,0.69,0.98,0.7,0.95,0.96,0.85,0.94,0.83,0.95,0.84,0.92,0.91,1,0.91,0.89,0.93,0.99,0.76,0.88,0.82,0.82,0.98,0.88,0.92,0.92,0.95,0.89,0.82,0.63,0.22],
  "v2-we-thank-ya-em-82-01": [1,0.6,0.76,0.55,0.65,0.64,0.64,0.68,0.74,0.68,0.66,0.68,0.88,0.84,0.56,0.69,0.58,0.78,0.56,0.43,0.66,0.62,0.58,0.59,0.78,0.66,0.77,0.58,0.51,0.6,0.64,0.42,0.29,0.2,0.08],
  "v3-rising-eb-82-50-01": [0.83,0.51,0.66,0.5,0.77,0.69,0.74,0.95,0.81,0.78,0.57,0.59,0.74,0.73,0.59,1,0.72,0.88,0.71,0.69,0.56,0.73,0.88,0.62,0.97,0.9,0.81,0.59,0.66,0.59,0.57,0.36,0.31,0.23,0.06],
  "v3-sing-song-abm-74-01": [0.8,0.52,0.57,0.59,0.87,0.58,0.54,0.55,0.79,0.76,0.9,0.87,0.64,0.84,0.77,1,0.64,0.84,0.61,0.51,0.54,0.77,0.74,0.49,0.58,0.61,0.71,0.64,1,0.63,0.73,0.3,0.18,0.18,0.05],
  "v3-talk-about-it-bbm-87-01": [0.74,0.73,0.76,0.75,0.77,0.7,0.57,0.69,0.71,0.79,0.63,0.8,1,0.82,0.71,0.54,0.51,0.72,0.86,0.64,0.85,0.68,0.76,0.69,0.59,0.69,0.91,0.84,0.69,0.98,0.76,0.53,0.42,0.18,0.04],
  "v3-the-fountain-abm-82-01": [0.76,0.75,0.9,0.55,0.59,0.34,0.46,0.38,0.49,0.76,0.81,0.81,0.53,0.72,0.73,0.93,0.56,0.8,0.55,0.87,0.67,0.44,0.44,0.28,0.51,0.32,0.43,0.9,1,0.48,0.37,0.38,0.36,0.15,0.05],
  "v4-dream-of-think-of-ebm-75-01": [0.68,0.83,0.79,0.5,0.7,0.83,0.79,0.58,0.62,0.73,0.82,0.69,0.49,0.77,0.63,0.69,0.52,0.66,0.72,0.9,0.85,0.73,0.78,0.59,0.7,0.51,0.72,0.83,0.75,0.8,1,0.64,0.38,0.23,0.06],
  "v4-let-myself-down-abm-77-01": [0.62,0.73,0.76,0.75,0.87,0.6,0.81,0.59,0.85,0.56,0.75,0.79,0.82,0.73,0.83,0.94,0.66,0.81,0.4,0.79,0.42,0.72,0.4,0.7,0.63,1,0.93,0.53,0.77,0.45,0.71,0.33,0.55,0.23,0.14],
  "v4-one-thousand-years-bm-84-01": [0.74,0.61,0.91,0.54,0.9,0.56,0.8,1,0.57,0.74,0.34,0.88,0.56,0.92,0.39,0.47,0.52,0.63,0.7,0.66,0.82,0.48,0.32,0.54,0.84,0.64,0.82,0.82,0.75,0.64,0.56,0.48,0.33,0.19,0.06],
  "v4-rest-now-e-75-01": [0.85,0.78,0.8,0.57,0.68,0.59,0.88,0.66,0.71,1,0.66,0.41,0.49,0.76,0.71,0.79,0.72,0.73,0.69,0.72,0.69,0.78,0.67,0.62,0.65,0.66,0.75,0.59,0.81,0.71,0.69,0.49,0.45,0.25,0.07],
};

// Initialize all waveforms with pre-defined data
function initializeWaveforms() {
  const containers = Array.from(document.querySelectorAll('[data-audio-waveform]'));
  const missing = [];

  containers.forEach((container) => {
    const audioId = container.dataset.audioWaveform;
    const data = waveformData[audioId];

    // Always call generateWaveformBars - it handles both cases:
    // - With data: generates new bars from waveformData
    // - Without data: makes existing Liquid placeholder bars visible
    generateWaveformBars(container, data);

    if (!data) {
      missing.push(audioId);
    }
  });

  // Helpful debug (shows up in Shopify theme preview console)
  if (missing.length) {
    console.warn('[Daybreak] Missing waveformData keys for:', missing);
  }
  console.info('[Daybreak] Waveform containers found:', containers.length);
}

// ============================================
// INITIALIZE ON DOM READY
// ============================================

function init() {
  new AudioPlayer();
  new StickyBar();
  new FAQAccordion();
  new LazyLoader();
  new MarqueeController();
  new ScrollAnimations();
  initializeScrubbers();
  initializeWaveforms();
}

// Guard against multiple initializations
if (window.DaybreakInitialized) return;
window.DaybreakInitialized = true;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// Export for external use if needed
window.Daybreak = {
  AudioPlayer,
  StickyBar,
  FAQAccordion,
  waveformData,
  generateWaveformBars
};

})();
